import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonComponent, SnackbarComponent } from '@predigle/micro/component-ui';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';
import { LoaderService } from '../../services/loader.service';
import { ClarityService } from '../../services/clarity.service';
import { MatIcon } from "@angular/material/icon";
import { LoaderComponent } from '../shared/reusable/loader/loader.component';
import { AppBannerComponent } from '../shared/reusable/app-banner/app-banner.component';
import { LoginSlideArtComponent } from './login-slide-art/login-slide-art.component';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, SnackbarComponent, MatIcon, LoaderComponent, AppBannerComponent, LoginSlideArtComponent],
  templateUrl: './login-page.component.html',
  styleUrl: './login-page.component.scss',
})
export class LoginPageComponent {
  view: 'login' | 'forgot' = 'login';

  // Login fields
  email = '';
  password = '';
  rememberMe = true;
  loading = false;
  error = '';
  showSnackbar = false;
  snackbarMessage = '';
  snackbarType: 'success' | 'error' | 'warning' = 'error';
  showPassword = false;
  formSubmitted = false;

  // Forgot password fields
  forgotEmail = '';
  forgotLoading = false;
  forgotSubmitted = false;

  // Active session dialog
  showActiveSessionDialog = false;

  // Account picker
  showAccountPicker = false;
  savedAccounts: { email: string; password: string }[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private loaderService: LoaderService,
    private clarityService: ClarityService
  ) {}

  login() {
    this.formSubmitted = true;
    if (!this.email.trim()) {
      this.showError('Email is required');
      return;
    }
    if (!this.password.trim()) {
      this.showError('Password is required');
      return;
    }

    this.loading = true;
    this.error = '';
    this.loaderService.show();
    this.authService.login(this.email, this.password).subscribe({
      next: (res) => {
        this.loading = false;
        this.loaderService.hide();
        if (res?.success) {
          if (this.rememberMe) {
            this.authService.saveCredentials(this.email.trim(), this.password);
          }
          const userId = this.authService.getUserObjectId() || this.authService.getUserIdFromToken() || '';
          this.clarityService.identifyUser(userId, this.email.trim());
          this.clarityService.trackEvent('login_success');
          this.clarityService.setAction('action_login');
          const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
          this.router.navigateByUrl(returnUrl);
        } else {
          this.clarityService.trackEvent('login_failed');
          this.showError(res?.message || 'Invalid email or password');
        }
      },
      error: (err) => {
        this.loading = false;
        this.loaderService.hide();
        if (err?.status === 403 && err?.error?.detail?.toLowerCase().includes('active session')) {
          this.showActiveSessionDialog = true;
          return;
        }
        const message = err?.error?.message || err?.error?.error || 'Invalid email or password';
        this.showError(message);
        console.error(err);
      },
    });
  }

  // ── Account Picker ──────────────────────────────────────────────────

  openAccountPicker(): void {
    this.savedAccounts = this.authService.loadSavedAccounts();
    if (this.savedAccounts.length > 0) {
      this.showAccountPicker = true;
    }
  }

  selectAccount(account: { email: string; password: string }): void {
    this.showAccountPicker = false;
    this.email = account.email;
    this.password = account.password;
    this.rememberMe = true;
    // auto-submit
    this.login();
  }

  removeAccount(email: string, event: Event): void {
    event.stopPropagation();
    this.authService.removeAccount(email);
    this.savedAccounts = this.authService.loadSavedAccounts();
    if (this.savedAccounts.length === 0) {
      this.showAccountPicker = false;
    }
  }

  addAccount(): void {
    if (this.savedAccounts.length >= this.authService.maxAccounts) {
      this.showError(`You can save up to ${this.authService.maxAccounts} accounts. Remove one to add another.`);
      return;
    }
    this.showAccountPicker = false;
  }

  closeAccountPicker(): void {
    this.showAccountPicker = false;
  }

  // ────────────────────────────────────────────────────────────────────

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  createProfile() {
    this.router.navigate(['/auth/create-profile']);
  }

  openForgotPassword() {
    this.forgotEmail = this.email;
    this.forgotSubmitted = false;
    this.view = 'forgot';
  }

  backToLogin() {
    this.view = 'login';
    this.forgotEmail = '';
    this.forgotSubmitted = false;
  }

  submitForgotPassword() {
    this.forgotSubmitted = true;
    if (!this.forgotEmail.trim()) {
      this.showError('Please enter your email address');
      return;
    }

    this.forgotLoading = true;
    this.authService.forgotPassword(this.forgotEmail).subscribe({
      next: (_res) => {
        this.forgotLoading = false;
        this.snackbarMessage = 'Password reset instructions sent to your email';
        this.snackbarType = 'success';
        this.showSnackbar = true;
        this.backToLogin();
      },
      error: (err) => {
        this.forgotLoading = false;
        const message = err?.error?.message || err?.error?.error || 'Failed to send reset email';
        this.showError(message);
      },
    });
  }

  activeSlide = 0;

  // Toggle: /login?slides=graphic swaps the photos for the animated SVG art. Photos stay the default.
  graphicSlides = false;

  slides = [
    {
      image: 'assets/login/photos/discover.webp',
      title: 'Discover',
      subtitle: 'Relive each outing with context, conditions, and insight; Turn every trip into its own narrative.',
    },
    {
      image: 'assets/login/photos/connect.webp',
      title: 'Connect',
      subtitle: 'Share trips, data, and moments with friends, crews, and trusted networks on your terms.',
    },
    {
      image: 'assets/login/photos/enhance.webp',
      title: 'Enhance',
      subtitle: 'See wind, tides, and surface currents on the map while your trips, observations, and waypoints are recorded for you.',
    },
  ];

  ngOnInit() {
    this.graphicSlides = this.route.snapshot.queryParams['slides'] === 'graphic';
    if (this.authService.isAuthenticated()) {
      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
      this.router.navigateByUrl(returnUrl);
      return;
    }
    setInterval(() => {
      this.activeSlide = (this.activeSlide + 1) % this.slides.length;
    }, 4000);

    // Show account picker if saved accounts exist (matches Flutter _loadSavedCredentials)
    const accounts = this.authService.loadSavedAccounts();
    if (accounts.length > 0) {
      this.savedAccounts = accounts;
      this.showAccountPicker = true;
    }
  }

  setSlide(index: number) {
    this.activeSlide = index;
  }

  private showError(message: string) {
    this.snackbarMessage = message;
    this.snackbarType = 'error';
    this.showSnackbar = true;
  }

  onSnackbarClose() {
    this.showSnackbar = false;
  }

  onSnackbarAutoClose() {
    this.showSnackbar = false;
  }
}
